// Picture: one picture on the timeline as an item with three stages (src/ui/stages): 1 Picture (the image maker,
// PictureMaker.tsx), 2 Faces (placing who is on screen with it) and 3 Animate (MotionPage.tsx). Faces and Animate wait for
// the first picture to arrive. Faces and Animate are optional: every stage's main button is Done, with small Fix faces
// and Animate buttons under it. A new picture opens on Picture; an existing one on its Overview, which says what each
// stage holds and has Delete picture at the foot. Everything saves as it changes, so back and Done just leave.
// A new picture has no marker until its first picture arrives; the page keeps the id it was opened with (`newId`) and
// finds the marker under it. Painting and animating keep going if the page is left (ui/jobs.ts).
import { useState } from "react";
import { imageSrc } from "../../../library/images";
import { useLibrary } from "../../../library/store";
import { findAsset } from "../../../model/library";
import { carryFaces, markById, marksOf, motionOf, removeMarker, setPictureFaces, type Placed, type PictureMark } from "../../../model/timeline";
import { FrameSvg } from "../../../render/FrameSvg";
import { Avatar, Icon, useIdleClock } from "../../common";
import { motionJob, picJob, useJob } from "../../jobs";
import { ItemActions, OverviewPeek, OverviewRow, OverviewRows, StagePage, useStageGo, type Stage, type StageAt } from "../../stages/StagePage";
import { useStyles } from "../../images/styles";
import { useTimeline } from "../TimelineLoader";
import { FacePlacing } from "./FacePlacing";
import { playName, MotionStage } from "./MotionPage";
import { keptVersions, PictureMaker } from "./PictureMaker";
import { frameWith, subLine } from "./shared";
import type { PageProps } from "./index";

type Step = "picture" | "faces" | "animate";
const STEPS: readonly string[] = ["overview", "picture", "faces", "animate"];
const stepOf = (s: string | undefined): StageAt<Step> | undefined => (STEPS.includes(s ?? "") ? (s as StageAt<Step>) : undefined);

export function PicturePage({ t, editId, newId, step, onClose }: PageProps) {
  const { ep } = useTimeline();
  const id = (editId ?? newId)!;
  const mark = markById(ep, "picture", id);
  const [at, setAt] = useState<StageAt<Step>>(stepOf(step) ?? (editId ? "overview" : "picture"));
  const [note, setNote] = useState<string | null>(null);
  const painting = useJob(picJob(id)) !== null;
  const animating = useJob(motionJob(id)) !== null;
  const motion = mark && motionOf(mark);

  // Faces and Animate need a picture to work on.
  const soon = (what: string, why: string) => (mark ? undefined : { reason: `${what} opens once V1 has arrived. ${why}` });
  const stages: Stage<Step>[] = [
    { key: "picture", label: "Picture", done: !!mark, working: painting },
    { key: "faces", label: "Faces", done: !!mark?.faces.length, optional: { button: "Fix faces", icon: "face" }, wait: soon("Faces", "The faces go on the picture, so there has to be one.") },
    { key: "animate", label: "Animate", done: !!motion, working: animating, optional: { button: "Animate", icon: "film" }, wait: soon("Animate", "It makes the picture move, so there has to be one.") },
  ];
  const n = mark ? marksOf(ep, "picture").findIndex(p => p.id === id) + 1 : 0;
  const length = mark && at === "animate" ? ` · ${(mark.end - mark.t).toFixed(1)} s` : "";

  return (
    <StagePage title={mark ? `Picture ${n}` : "New picture"} sub={`${subLine(mark?.t ?? t, !!mark)}${length}`} onBack={onClose} onDone={onClose}
      stages={stages} at={at} onAt={setAt} className={at === "picture" ? "mk pp" : at === "animate" ? "mk an-page" : undefined} toast={note} clearToast={() => setNote(null)}>
      {at === "overview" && (mark ? <PictureOverview mark={mark} stages={stages} onClose={onClose} /> : <p className="hint">This picture is gone.</p>)}
      {at === "picture" && <PictureMaker t={t} id={id} onNote={setNote} />}
      {at === "faces" && <PictureFaces id={id} />}
      {at === "animate" && <MotionStage id={id} />}
    </StagePage>
  );
}

// What each stage holds now, one row each, then the picture's own actions.
function PictureOverview({ mark, stages, onClose }: { mark: Placed<PictureMark>; stages: Stage<Step>[]; onClose: () => void }) {
  const { ep, update } = useTimeline();
  const lib = useLibrary();
  const clock = useIdleClock();
  const styles = useStyles();
  const go = useStageGo<Step>();
  const waiting = (k: Step) => !!stages.find(s => s.key === k)?.wait;
  const versions = keptVersions(mark);
  const v = Math.max(1, versions.findIndex(x => x.picture.src === mark.picture.src) + 1);
  const style = styles.find(s => s.text === mark.style)?.name;
  const names = mark.faces.map(f => findAsset(lib, f.char)?.name ?? "?");
  const motion = motionOf(mark);
  const m = mark.motions?.findIndex(x => x.id === motion?.id) ?? -1;
  return (
    <>
      <OverviewPeek><FrameSvg id="tl-picover" boil={false} frame={frameWith(ep, mark.t, clock)} /></OverviewPeek>
      <OverviewRows>
        <OverviewRow thumb={<img src={imageSrc(mark.picture.src)} alt="" />} title="Picture" onClick={() => go("picture")}
          sub={`V${v} of ${versions.length}${style ? ` · ${style}` : ""}`} />
        <OverviewRow round thumb={mark.faces[0] ? <Avatar assetId={mark.faces[0].char} size={44} /> : <Icon name="person" size={24} />} title="Faces" onClick={() => go("faces")}
          sub={names.length ? names.join(", ") : "Nobody yet"} waiting={waiting("faces")} />
        <OverviewRow thumb={<img src={imageSrc(motion?.frames[0]?.picture.src ?? mark.picture.src)} alt="" />} title="Animate" onClick={() => go("animate")}
          sub={motion ? `V${m + 1} · ${playName(motion.play)}` : "Still · make it move"} waiting={waiting("animate")} />
      </OverviewRows>
      <ItemActions title="This picture">
        <button className="btn danger text" onClick={() => { update(e => removeMarker(e, "picture", mark.id)); onClose(); }}><Icon name="trash" /> Delete picture</button>
      </ItemActions>
    </>
  );
}

// Faces: who is on screen with this picture, and where. The faces belong to the picture, so they show from its start to
// its end. The placing is the shared Placer, through FacePlacing; every move is saved as it is made.
function PictureFaces({ id }: { id: string }) {
  const { ep, update } = useTimeline();
  const mark = markById(ep, "picture", id);
  if (!mark) return null;
  // Copied from the previous picture: this picture's saved faces are exactly that picture's faces, carried over.
  const before = marksOf(ep, "picture").filter(p => p.t < mark.t - 1e-6).pop();
  const copied = !!before && before.faces.length > 0 && JSON.stringify(mark.faces) === JSON.stringify(carryFaces(before.faces, before.picture, mark.picture));
  return (
    <FacePlacing ep={ep} t={mark.t} picture={mark.picture} faces={mark.faces} refs={mark.refs}
      onFaces={faces => update(e => setPictureFaces(e, id, faces), { merge: `faces-${id}` })}
      hint={copied ? "Faces copied from the previous picture. Move any that are wrong." : undefined} />
  );
}
